import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { X, User, Calendar, MapPin, Phone, Mail, FileText, AlertCircle } from 'lucide-react';
import { get } from '../../api/apiClient';
import SearchableDropdown from './SearchableDropdown';

const locationsList = [
  { value: 'delhi', label: 'Delhi' },
  { value: 'noida', label: 'Noida' },
  { value: 'gurugram', label: 'Gurugram' },
  { value: 'work_from_home', label: 'Work From Home' }
];

const capitalizeText = (str) => {
  if (!str) return '';
  return str
    .replace(/[_-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const WfhApplyModal = ({
  isOpen,
  onClose,
  onSubmit,
  isAssign = false,
  submitting = false
}) => {
  const [form, setForm] = useState({
    employeeId: '',
    emergencyContactName: '',
    emergencyContactEmail: '',
    emergencyContactPhone: '',
    workLocation: '',
    wfhType: 'single_day',
    fromDate: '',
    toDate: '',
    reasonCategory: '',
    reason: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false); // Track if search has been triggered at least once

  const resetForm = () => {
    setForm({
      employeeId: '',
      emergencyContactName: '',
      emergencyContactEmail: '',
      emergencyContactPhone: '',
      workLocation: '',
      wfhType: 'single_day',
      fromDate: '',
      toDate: '',
      reasonCategory: '',
      reason: ''
    });
    setFieldErrors({});
    setEmployeeSearchTerm('');
    setDebouncedEmployeeSearch('');
    setEmployeeSearchOpen(false);
    setHasSearchedOnce(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
      // Open search for initial load of employees
      if (isAssign) {
        setEmployeeSearchOpen(true);
        setHasSearchedOnce(true);
      }
    }
  }, [isOpen, isAssign]);

  // Debounce employee search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Query: Locations
  const { data: activeLocations = [] } = useQuery({
    queryKey: ['activeLocations'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/locations/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      return result?.success ? result.data || [] : [];
    },
    enabled: isOpen
  });

  const locations = useMemo(() => {
    if (activeLocations.length > 0) {
      return activeLocations.map(loc => ({
        value: loc.value || loc.label || loc.code || '',
        label: capitalizeText(loc.label || loc.value || loc.code || '')
      }));
    }
    return locationsList.map(loc => ({
      ...loc,
      label: capitalizeText(loc.label)
    }));
  }, [activeLocations]);

  // Query: WFH Reason Categories
  const { data: wfhCategories = [], isLoading: loadingCategories } = useQuery({
    queryKey: ['wfhReasonCategories'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/config/wfh-reason-categories/dropdown`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      return result?.success ? result.data || [] : [];
    },
    enabled: isOpen
  });

  // Query: Employees List with Search and Pagination (Only if isAssign is true)
  const { data: employeesList = [], isFetching: loadingEmployees } = useQuery({
    queryKey: ['wfh-assign-employees-search', debouncedEmployeeSearch],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const trimmedSearch = debouncedEmployeeSearch?.trim() || '';
      const url = `${process.env.REACT_APP_API_BASE_URL || '/api'}/dropdown-data/employees?search=${encodeURIComponent(trimmedSearch)}&limit=50`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      if (result.responseCode === 200) {
        const employees = result.data?.employees || [];
        console.log('API Response - Employees:', employees);
        if (employees.length > 0) {
          console.log('First employee sample:', employees[0]);
        }
        return employees;
      }
      return [];
    },
    enabled: isOpen && isAssign && (employeeSearchOpen || hasSearchedOnce),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes to prevent unnecessary refetches
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || localStorage.getItem('user_data') || '{}');
    } catch {
      return {};
    }
  }, []);

  const userRole = (storedUser?.role || '').toLowerCase();
  const userDesignation = (storedUser?.designation || storedUser?.position || '').toLowerCase();
  const isTraineeOrIntern = userRole === 'trainee' || userRole === 'intern' || userRole.includes('trainee') || userRole.includes('intern') || userDesignation.includes('trainee') || userDesignation.includes('intern');

  const employeeOptions = useMemo(() => {
    return employeesList
      .filter(emp => {
        const r = (emp.role || emp.user?.role || '').toLowerCase();
        const d = (emp.employmentInfo?.designation || emp.employmentInfo?.position || emp.designation || '').toLowerCase();
        const t = (emp.employmentInfo?.employmentType || '').toLowerCase();
        return r !== 'trainee' && r !== 'intern' && !r.includes('trainee') && !r.includes('intern') && !d.includes('trainee') && !d.includes('intern') && !t.includes('trainee') && !t.includes('intern');
      })
      .map(emp => {
      // Build full name from personalInfo
      const firstName = emp.personalInfo?.firstName || '';
      const lastName = emp.personalInfo?.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      // If no name available, use employeeId as fallback
      const displayName = fullName || emp.employeeId || 'Unknown Employee';
      
      // Get role and format it
      const roleStr = emp.role ? emp.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Employee';
      
      // Get department and position
      const department = emp.employmentInfo?.department || 'N/A';
      const position = emp.employmentInfo?.position || '';
      
      // Get emails - officialEmail is at root level, personalEmail is in contactInfo
      const officialEmail = emp.officialEmail || '';
      const personalEmail = emp.contactInfo?.personalEmail || '';
      const displayEmail = officialEmail || personalEmail || '-';
      
      console.log(`Mapping employee: ${displayName}, ID: ${emp.employeeId}, officialEmail: ${officialEmail}, personalEmail: ${personalEmail}, dept: ${department}`);
      
      return {
        id: emp.id || emp._id,
        displayText: displayName, // Show just the name without role in brackets
        searchText: `${displayName} ${officialEmail} ${personalEmail} ${emp.employeeId || ''} ${roleStr} ${department} ${position}`.toLowerCase(),
        title: displayName,
        raw: emp,
        role: roleStr,
        department: department,
        position: position,
        employeeId: emp.employeeId || '',
        officialEmail: officialEmail,
        personalEmail: personalEmail,
        displayEmail: displayEmail,
        renderOption: (opt) => (
          <div className="flex flex-col py-0.5 text-left">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{opt.title}</span>
              {opt.employeeId && (
                <span className="text-xs text-gray-500">({opt.employeeId})</span>
              )}
            </div>
            <div className="flex items-center text-[11px] mt-0.5 space-x-2">
              <span className="px-1.5 py-0.5 bg-blue-50 text-[#0F2942] rounded font-medium uppercase tracking-wider text-[10px]">{opt.role}</span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-500 italic">{opt.department}</span>
              {opt.position && (
                <>
                  <span className="text-slate-400">•</span>
                  <span className="text-slate-500">{opt.position}</span>
                </>
              )}
            </div>
          </div>
        )
      };
    });
  }, [employeesList]);

  const selectedEmployee = useMemo(() => {
    if (!form.employeeId) return null;
    const employee = employeesList.find(emp => (emp.id || emp._id) === form.employeeId) || null;
    
    // Add logging to debug
    if (employee) {
      console.log('Selected employee data:', employee);
      console.log('PersonalInfo:', employee.personalInfo);
    }
    
    return employee;
  }, [employeesList, form.employeeId]);

  const validateField = (name, value) => {
    let error = '';
    const val = typeof value === 'string' ? value.trim() : value;
    switch (name) {
      case 'employeeId':
        if (isAssign && !val) error = 'Employee selection is required.';
        break;
      case 'workLocation':
        // Work location is now optional - no validation needed
        break;
      case 'fromDate':
        if (!val) error = 'Date is required.';
        break;
      case 'toDate':
        if (form.wfhType === 'multiple_days' && !val) {
          error = 'To date is required.';
        }
        break;
      case 'reasonCategory':
        if (!val) error = 'Reason category is required.';
        break;
      case 'reason':
        if (!val) error = 'Reason details are required.';
        break;
      case 'emergencyContactEmail':
        if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
          error = 'Invalid email address.';
        }
        break;
      case 'emergencyContactPhone':
        if (val && val.length !== 10) {
          error = 'Phone must be exactly 10 digits.';
        }
        break;
      default:
        break;
    }
    setFieldErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const handleBlur = (name) => {
    validateField(name, form[name]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isAssign && isTraineeOrIntern) {
      return;
    }
    const fieldsToValidate = [
      'fromDate',
      'reasonCategory',
      'reason',
      'emergencyContactEmail',
      'emergencyContactPhone'
    ];
    if (isAssign) {
      fieldsToValidate.push('employeeId');
    }
    if (form.wfhType === 'multiple_days') {
      fieldsToValidate.push('toDate');
    }

    const errors = {};
    fieldsToValidate.forEach(name => {
      const err = validateField(name, form[name]);
      if (err) {
        errors[name] = err;
      }
    });

    if (Object.keys(errors).length > 0) {
      return;
    }

    const finalToDate = form.wfhType === 'multiple_days' ? form.toDate : form.fromDate;
    const payload = {
      emergencyContact: {
        name: form.emergencyContactName,
        email: form.emergencyContactEmail,
        phone: form.emergencyContactPhone
      },
      workLocation: form.workLocation,
      wfhType: form.wfhType,
      fromDate: form.fromDate,
      toDate: finalToDate,
      reasonCategory: form.reasonCategory,
      reason: form.reason
    };

    if (isAssign) {
      payload.employeeId = form.employeeId;
    }

    onSubmit(payload);
  };

  if (!isOpen) return null;

  const todayISO = new Date().toISOString().split('T')[0];

  return createPortal(
    <div className="fixed inset-0 z-[99999] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl border border-gray-100 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden transform transition-all scale-100 duration-300 animate-in zoom-in-95 slide-in-from-bottom-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overflow-y-auto max-h-[90vh] smooth-scroll">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white rounded-t-2xl sticky top-0 z-10">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              {isAssign ? 'Assign Work From Home' : 'Apply for Work From Home'}
            </h3>
            <p className="text-xs text-gray-400">Fill in the work details and contact info below</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5 text-sm">
            {!isAssign && isTraineeOrIntern && (
              <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Work From Home is not applicable for Trainees or Interns as per company policy.</span>
              </div>
            )}

            {/* Select Employee dropdown (Only for Assign mode) */}
            {isAssign && (
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Select Employee <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  options={employeeOptions}
                  value={form.employeeId || null}
                  onChange={(selectedId) => {
                    console.log('Employee selected:', selectedId);
                    setForm(prev => ({ ...prev, employeeId: selectedId || '' }));
                    setFieldErrors(prev => ({ ...prev, employeeId: '' }));
                    setEmployeeSearchOpen(false); // Close search after selection
                  }}
                  onSearch={(searchValue) => {
                    console.log('Search triggered:', searchValue);
                    setEmployeeSearchOpen(true);
                    setEmployeeSearchTerm(searchValue || '');
                    setHasSearchedOnce(true);
                  }}
                  placeholder={loadingEmployees ? 'Loading...' : 'Type to search employees...'}
                  loading={loadingEmployees}
                  searchPlaceholder="Search by name, email, employee ID, department, or role..."
                  matchMode="contains"
                  minSearchLength={0}
                  className="w-full text-sm"
                  onBlur={() => handleBlur('employeeId')}
                  noResultsText={!employeeSearchOpen && !hasSearchedOnce ? 'Start typing to search employees' : 'No employees found'}
                />
                {fieldErrors.employeeId && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {fieldErrors.employeeId}
                  </p>
                )}
                {selectedEmployee && (
                  <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/50 p-3">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-100 text-[#0B2033] flex items-center justify-center font-semibold text-sm flex-shrink-0">
                        {(selectedEmployee.personalInfo?.firstName || selectedEmployee.employeeId || 'E').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {`${selectedEmployee.personalInfo?.firstName || ''} ${selectedEmployee.personalInfo?.lastName || ''}`.trim() || selectedEmployee.employeeId || 'Employee'}
                        </p>
                        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                          <span className="truncate">ID: {selectedEmployee.employeeId || '-'}</span>
                          <span className="truncate">Email: {selectedEmployee.officialEmail || selectedEmployee.contactInfo?.personalEmail || '-'}</span>
                          <span className="truncate">Department: {selectedEmployee.employmentInfo?.department || '-'}</span>
                          <span className="truncate">Role: {selectedEmployee.role ? capitalizeText(selectedEmployee.role) : '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Emergency Contact details */}
            <div className="border border-gray-150 rounded-xl p-4 bg-gray-50/50 space-y-3 transition-all duration-200">
              <span className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                Emergency Contact Details
              </span>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1 transition-all duration-200">
                  <label className="block text-[11px] font-medium text-gray-500">Contact Name</label>
                  <input 
                    type="text" 
                    value={form.emergencyContactName}
                    onChange={(e) => setForm({ ...form, emergencyContactName: e.target.value.slice(0, 100) })}
                    maxLength={100}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white transition-all duration-200"
                    placeholder="Full Name" 
                  />
                  <div className="text-right text-[9px] text-gray-400 mt-0.5">
                    {form.emergencyContactName ? form.emergencyContactName.length : 0} / 100
                  </div>
                </div>

                <div className="space-y-1 transition-all duration-200">
                  <label className="block text-[11px] font-medium text-gray-500">Contact Email</label>
                  <input 
                    type="email" 
                    value={form.emergencyContactEmail}
                    onChange={(e) => { 
                      setForm({ ...form, emergencyContactEmail: e.target.value.slice(0, 256) }); 
                      setFieldErrors(prev => ({ ...prev, emergencyContactEmail: '' })); 
                    }}
                    onBlur={() => handleBlur('emergencyContactEmail')}
                    maxLength={256}
                    className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white transition-all duration-200 ${fieldErrors.emergencyContactEmail ? 'border-red-400 ring-2 ring-red-100' : 'border-gray-200'}`}
                    placeholder="email@example.com" 
                  />
                  {fieldErrors.emergencyContactEmail ? (
                    <p className="text-[10px] text-red-500 mt-0.5 animate-in slide-in-from-top-1">{fieldErrors.emergencyContactEmail}</p>
                  ) : (
                    <div className="text-right text-[9px] text-gray-400 mt-0.5">
                      {form.emergencyContactEmail ? form.emergencyContactEmail.length : 0} / 256
                    </div>
                  )}
                </div>

                <div className="space-y-1 transition-all duration-200">
                  <label className="block text-[11px] font-medium text-gray-500">Contact Phone</label>
                  <input 
                    type="text" 
                    value={form.emergencyContactPhone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setForm({ ...form, emergencyContactPhone: val });
                      setFieldErrors(prev => ({ ...prev, emergencyContactPhone: '' }));
                    }}
                    onBlur={() => handleBlur('emergencyContactPhone')}
                    maxLength={10}
                    className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white transition-all duration-200 ${fieldErrors.emergencyContactPhone ? 'border-red-400 ring-2 ring-red-100' : 'border-gray-200'}`}
                    placeholder="10-digit number" 
                  />
                  {fieldErrors.emergencyContactPhone ? (
                    <p className="text-[10px] text-red-500 mt-0.5 animate-in slide-in-from-top-1">{fieldErrors.emergencyContactPhone}</p>
                  ) : (
                    <div className="text-right text-[9px] text-gray-400 mt-0.5">
                      {form.emergencyContactPhone ? form.emergencyContactPhone.length : 0} / 10
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Location and Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Work Location
                </label>
                <select 
                  value={form.workLocation}
                  onChange={(e) => { 
                    setForm({ ...form, workLocation: e.target.value }); 
                    setFieldErrors(prev => ({ ...prev, workLocation: '' })); 
                  }}
                  onBlur={() => handleBlur('workLocation')}
                  className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white text-slate-800 transition-all duration-200 ${fieldErrors.workLocation ? 'border-red-400 ring-2 ring-red-100' : 'border-gray-200'}`}
                >
                  <option value="" className="text-slate-800 bg-white">-- Choose Location (Optional) --</option>
                  {locations.map((loc, idx) => (
                    <option key={idx} value={loc.value} className="text-slate-800 bg-white">
                      {loc.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.workLocation && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1 animate-in slide-in-from-top-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {fieldErrors.workLocation}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Reason Category <span className="text-red-500">*</span>
                </label>
                <select 
                  value={form.reasonCategory}
                  onChange={(e) => { 
                    setForm({ ...form, reasonCategory: e.target.value }); 
                    setFieldErrors(prev => ({ ...prev, reasonCategory: '' })); 
                  }}
                  onBlur={() => handleBlur('reasonCategory')}
                  className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white text-slate-800 ${fieldErrors.reasonCategory ? 'border-red-400' : 'border-gray-200'}`}
                >
                  <option value="" className="text-slate-800 bg-white">{loadingCategories ? 'Loading Categories...' : '-- Choose Category --'}</option>
                  {wfhCategories.map((cat) => (
                    <option key={cat._id} value={cat._id} className="text-slate-800 bg-white">
                      {capitalizeText(cat.name)}
                    </option>
                  ))}
                </select>
                {fieldErrors.reasonCategory && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {fieldErrors.reasonCategory}
                  </p>
                )}
              </div>
            </div>

            {/* WFH Type */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                WFH Type <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6 mt-1 text-xs">
                {[
                  { value: 'single_day', label: 'Single Day' }, 
                  { value: 'multiple_days', label: 'Multiple Days' }
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-gray-700">
                    <input 
                      type="radio" 
                      name="wfhType" 
                      value={opt.value}
                      checked={form.wfhType === opt.value}
                      onChange={(e) => setForm({ ...form, wfhType: e.target.value, toDate: '' })}
                      className="text-[#0F2942] focus:ring-blue-400 h-4 w-4" 
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {form.wfhType === 'multiple_days' ? 'From Date' : 'Date'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input 
                    type="date" 
                    value={form.fromDate}
                    min={todayISO}
                    onChange={(e) => { 
                      setForm({ ...form, fromDate: e.target.value, toDate: '' }); 
                      setFieldErrors(prev => ({ ...prev, fromDate: '', toDate: '' })); 
                    }}
                    onBlur={() => handleBlur('fromDate')}
                    onClick={(e) => e.target.showPicker?.()}
                    className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white cursor-pointer ${fieldErrors.fromDate ? 'border-red-400' : 'border-gray-200'}`} 
                  />
                </div>
                {fieldErrors.fromDate && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {fieldErrors.fromDate}
                  </p>
                )}
              </div>

              {form.wfhType === 'multiple_days' && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input 
                      type="date" 
                      value={form.toDate}
                      min={form.fromDate || todayISO}
                      onChange={(e) => { 
                        setForm({ ...form, toDate: e.target.value }); 
                        setFieldErrors(prev => ({ ...prev, toDate: '' })); 
                      }}
                      onBlur={() => handleBlur('toDate')}
                      onClick={(e) => e.target.showPicker?.()}
                      className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white cursor-pointer ${fieldErrors.toDate ? 'border-red-400' : 'border-gray-200'}`} 
                    />
                  </div>
                  {fieldErrors.toDate && (
                    <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {fieldErrors.toDate}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Reason Details */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Reason Details <span className="text-red-500">*</span>
              </label>
              <textarea 
                value={form.reason}
                onChange={(e) => { 
                  setForm({ ...form, reason: e.target.value.slice(0, 2000) }); 
                  setFieldErrors(prev => ({ ...prev, reason: '' })); 
                }}
                onBlur={() => handleBlur('reason')}
                maxLength={2000}
                rows={3}
                className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white resize-none ${fieldErrors.reason ? 'border-red-400' : 'border-gray-200'}`}
                placeholder="Provide detailed description..." 
              />
              {fieldErrors.reason ? (
                <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {fieldErrors.reason}
                </p>
              ) : (
                <div className="text-right text-[9px] text-gray-400 mt-0.5">
                  {form.reason ? form.reason.length : 0} / 2000
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl sticky bottom-0">
            <button 
              type="button" 
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={submitting || (!isAssign && isTraineeOrIntern)}
              className="px-4 py-2 text-xs bg-[#0F2942] text-white rounded-lg font-medium hover:bg-[#0B2033] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && (
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
              )}
              {submitting ? 'Submitting...' : isAssign ? 'Assign WFH' : 'Apply WFH'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WfhApplyModal;
